"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.convertMinMaxFromPercentToNumber = exports.getShowInPercentConfiguration = exports.setDrillConfigToXAxis = exports.getParentAttributeConfiguration = exports.getStackMeasuresConfiguration = exports.getYAxisConfiguration = exports.getSanitizedStackingForSeries = void 0;
var tslib_1 = require("tslib");
// (C) 2007-2021 GoodData Corporation
var partial_1 = tslib_1.__importDefault(require("lodash/partial"));
var merge_1 = tslib_1.__importDefault(require("lodash/merge"));
var includes_1 = tslib_1.__importDefault(require("lodash/includes"));
var isNil_1 = tslib_1.__importDefault(require("lodash/isNil"));
var set_1 = tslib_1.__importDefault(require("lodash/set"));
var isArray_1 = tslib_1.__importDefault(require("lodash/isArray"));
var dataLabelsHelpers_1 = require("./dataLabelsHelpers");
var common_1 = require("../_util/common");
var comboChartOptions_1 = require("../comboChart/comboChartOptions");
var isPrimaryYAxis_1 = require("./isPrimaryYAxis");
var chartCapabilities_1 = require("../_chartOptions/chartCapabilities");
/**
 * Set 'normal' stacking config to single series which will overwrite config in 'plotOptions.series'
 */
function handleStackMeasure(stackMeasures, seriesItem) {
    return stackMeasures
        ? tslib_1.__assign(tslib_1.__assign({}, seriesItem), { stacking: "normal", stack: seriesItem.yAxis }) : seriesItem;
}
/**
 * Set 'percent' stacking config to single series which will overwrite config in 'plotOptions.series'
 */
function handleStackMeasuresToPercent(stackMeasuresToPercent, seriesItem) {
    return stackMeasuresToPercent
        ? tslib_1.__assign(tslib_1.__assign({}, seriesItem), { stacking: "percent", stack: seriesItem.yAxis }) : seriesItem;
}
function getStackingValue(chartOptions, seriesItem) {
    var yAxes = chartOptions.yAxes, type = chartOptions.type;
    var stacking = seriesItem.stacking, yAxis = seriesItem.yAxis;
    var seriesChartType = seriesItem.type || type;
    var defaultStackingValue = common_1.isComboChart(type) ? null : "normal";
    return isPrimaryYAxis_1.isPrimaryYAxis(yAxes[yAxis]) && !common_1.isLineChart(seriesChartType) ? stacking : defaultStackingValue;
}
function handleDualAxis(chartOptions, seriesItem) {
    var yAxes = chartOptions.yAxes, type = chartOptions.type;
    var isDualAxis = yAxes.length === 2;
    if (!isDualAxis && !common_1.isComboChart(type)) {
        return seriesItem;
    }
    var stacking = seriesItem.stacking;
    // highcharts stack config
    // percent stack is only applied to primary Y axis
    var hcStackingConfig = stacking ? { stacking: getStackingValue(chartOptions, seriesItem) } : {};
    return tslib_1.__assign(tslib_1.__assign({}, seriesItem), hcStackingConfig);
}
function handleLabelStyle(chartOptions, seriesItem) {
    if (!common_1.isComboChart(chartOptions.type)) {
        return seriesItem;
    }
    var type = seriesItem.type, stacking = seriesItem.stacking;
    return tslib_1.__assign(tslib_1.__assign({}, seriesItem), { dataLabels: {
            style: dataLabelsHelpers_1.getLabelStyle(type, stacking),
        } });
}
function countMeasuresInSeries(series) {
    return series.reduce(function (result, seriesItem) {
        result[seriesItem.yAxis] += 1;
        return result;
    }, [0, 0]);
}
/**
 * For y axis having one series, this series should be removed stacking config
 */
function getSanitizedStackingForSeries(series) {
    var _a = countMeasuresInSeries(series), primaryMeasuresNum = _a[0], secondaryMeasuresNum = _a[1];
    /**
     * stackMeasures is applied for both measures in each axis
     * stackMeasuresToPercent is applied for
     * - [measures on primary   y-axis only] or
     * - [measures on secondary y-axis only] or
     * - [applied for measures on primary y-axis + ignore for measures on secondary y-axis]
     */
    // has measures on both [primary y-axis] and [secondary y-axis]
    if (primaryMeasuresNum > 0 && secondaryMeasuresNum > 0) {
        return series.map(function (seriesItem) {
            // seriesItem is on [secondary y-axis]
            if (seriesItem.yAxis === 1) {
                return tslib_1.__assign(tslib_1.__assign({}, seriesItem), { stack: null, 
                    // reset stackMeasuresToPercent in this case (stacking: PERCENT_STACK)
                    stacking: seriesItem.stacking ? "normal" : null });
            }
            else {
                return seriesItem;
            }
        });
    }
    // has [measures on primary y-axis only] or [measures on secondary y-axis only]
    return series;
}
exports.getSanitizedStackingForSeries = getSanitizedStackingForSeries;
function getSeriesConfiguration(chartOptions, config, stackMeasures, stackMeasuresToPercent) {
    var series = config.series;
    var handlers = [
        partial_1.default(handleStackMeasure, stackMeasures),
        partial_1.default(handleStackMeasuresToPercent, stackMeasuresToPercent),
        partial_1.default(handleDualAxis, chartOptions),
        partial_1.default(handleLabelStyle, chartOptions),
    ];
    // get series with stacking config
    var seriesWithStackingConfig = series.map(function (seriesItem) {
        return handlers.reduce(function (result, handler) { return handler(result); }, seriesItem);
    });
    return {
        series: getSanitizedStackingForSeries(seriesWithStackingConfig),
    };
}
function getYAxisConfiguration(chartOptions, config, chartConfig) {
    var _a;
    var type = common_1.getPrimaryChartType(chartOptions);
    var yAxis;
    yAxis = config.yAxis;
    if (!isArray_1.default(yAxis)) {
        yAxis = [yAxis];
    }
    var _b = chartConfig.stackMeasuresToPercent, stackMeasuresToPercent = _b === void 0 ? false : _b;
    // only support column char
    // bar chart disables stack labels by default
    if (!common_1.isColumnChart(type)) {
        return {};
    }
    var labelsVisible = (_a = chartConfig.dataLabels) === null || _a === void 0 ? void 0 : _a.visible;
    var dataLabelEnabled = dataLabelsHelpers_1.getLabelsVisibilityConfig(labelsVisible).enabled;
    // enable by default or follow dataLabels.visible config
    var stackLabelConfig = isNil_1.default(dataLabelEnabled) || dataLabelEnabled;
    var yAxisWithStackLabel = yAxis.map(function (axis, index) {
        // disable stack labels for primary Y axis when there is 'Stack to 100%' on
        var stackLabelEnabled = (index !== 0 || !stackMeasuresToPercent) && stackLabelConfig;
        return tslib_1.__assign(tslib_1.__assign({}, axis), { stackLabels: {
                enabled: stackLabelEnabled,
            } });
    });
    return { yAxis: yAxisWithStackLabel };
}
exports.getYAxisConfiguration = getYAxisConfiguration;
/**
 * Set config to highchart for 'Stack Measures' and 'Stack to 100%'
 */
function getStackMeasuresConfiguration(chartOptions, config, chartConfig) {
    var _a, _b;
    var _c = chartConfig.stackMeasures, stackMeasures = _c === void 0 ? false : _c, _d = chartConfig.stackMeasuresToPercent, stackMeasuresToPercent = _d === void 0 ? false : _d;
    var canStackInPercent = comboChartOptions_1.canComboChartBeStackedInPercent((_b = (_a = chartOptions.data) === null || _a === void 0 ? void 0 : _a.series) !== null && _b !== void 0 ? _b : []);
    if (!stackMeasures && !stackMeasuresToPercent) {
        return {};
    }
    return tslib_1.__assign(tslib_1.__assign({}, getSeriesConfiguration(chartOptions, config, stackMeasures, stackMeasuresToPercent && canStackInPercent)), getYAxisConfiguration(chartOptions, config, chartConfig));
}
exports.getStackMeasuresConfiguration = getStackMeasuresConfiguration;
/**
 * Add style to X axis in case of 'grouped-categories'
 */
function getParentAttributeConfiguration(chartOptions, config) {
    var type = chartOptions.type;
    var xAxis = config.xAxis;
    var xAxisItem = xAxis[0]; // expect only one X axis
    // parent attribute in X axis
    var parentAttributeOptions = {};
    // only apply font-weight to parent label
    set_1.default(parentAttributeOptions, "style", {
        fontWeight: "bold",
    });
    if (common_1.isInvertedChartType(type)) {
        // distance more 5px for two groups of attributes for bar chart
        set_1.default(parentAttributeOptions, "x", -5);
    }
    // 'groupedOptions' is custom property in 'grouped-categories' plugin
    set_1.default(xAxisItem, "labels.groupedOptions", [parentAttributeOptions]);
    return { xAxis: [xAxisItem] };
}
exports.getParentAttributeConfiguration = getParentAttributeConfiguration;
function setDrillConfigToXAxis(drillConfig) {
    return { xAxis: [{ drillConfig: drillConfig }] };
}
exports.setDrillConfigToXAxis = setDrillConfigToXAxis;
/**
 * Format labels in Y axis from '0 - 100' to '0% - 100%'
 * Only applied when measure/series in Y axis more than one
 */
function getShowInPercentConfiguration(chartOptions, chartConfig, _config) {
    var _a;
    if (chartOptions === void 0) { chartOptions = {}; }
    if (_config === void 0) { _config = {}; }
    var _b = chartConfig.stackMeasuresToPercent, stackMeasuresToPercent = _b === void 0 ? false : _b, primaryChartType = chartConfig.primaryChartType;
    var canStackInPercent = comboChartOptions_1.canComboChartBeStackedInPercent((_a = chartOptions.data) === null || _a === void 0 ? void 0 : _a.series);
    if (!canStackInPercent || !stackMeasuresToPercent || common_1.isLineChart(primaryChartType)) {
        return {};
    }
    var _c = chartOptions.yAxes, yAxes = _c === void 0 ? [] : _c, type = chartOptions.type;
    var percentageFormatter = partial_1.default(dataLabelsHelpers_1.formatAsPercent, 1);
    // suppose that max number of y axes is 2
    // percentage format only supports primary axis
    var yAxis = yAxes.map(function (axis, index) {
        if (index !== 0 || (common_1.isComboChart(type) && !isPrimaryYAxis_1.isPrimaryYAxis(axis))) {
            return {};
        }
        return {
            labels: {
                formatter: percentageFormatter,
            },
        };
    });
    return { yAxis: yAxis };
}
exports.getShowInPercentConfiguration = getShowInPercentConfiguration;
/**
 * Convert [0, 1] to [0, 100], it's needed by highchart
 * Only applied to primary Y axis
 */
function convertMinMaxFromPercentToNumber(_chartOptions, config, chartConfig) {
    var _a = chartConfig.stackMeasuresToPercent, stackMeasuresToPercent = _a === void 0 ? false : _a;
    if (!stackMeasuresToPercent) {
        return {};
    }
    var yAxes;
    yAxes = config.yAxis;
    if (!isArray_1.default(yAxes)) {
        yAxes = [yAxes];
    }
    var yAxis = yAxes.map(function (axis, _, axes) {
        var min = axis.min, max = axis.max;
        var newAxis = {};
        if (!isNil_1.default(min)) {
            set_1.default(newAxis, "min", min * 100);
        }
        if (!isNil_1.default(max)) {
            set_1.default(newAxis, "max", max * 100);
        }
        var numberOfAxes = axes.length;
        if (numberOfAxes === 1) {
            return newAxis;
        }
        var _a = axis.opposite, opposite = _a === void 0 ? false : _a;
        return opposite ? {} : newAxis;
    });
    return { yAxis: yAxis };
}
exports.convertMinMaxFromPercentToNumber = convertMinMaxFromPercentToNumber;
function getOptionalStackingConfiguration(chartOptions, config, chartConfig, drillConfig) {
    if (chartConfig === void 0) { chartConfig = {}; }
    var type = chartOptions.type;
    return includes_1.default(chartCapabilities_1.supportedStackingAttributesChartTypes, type)
        ? merge_1.default({}, setDrillConfigToXAxis(drillConfig), getParentAttributeConfiguration(chartOptions, config), getStackMeasuresConfiguration(chartOptions, config, chartConfig), getShowInPercentConfiguration(chartOptions, chartConfig, config), convertMinMaxFromPercentToNumber(chartOptions, config, chartConfig))
        : {};
}
exports.default = getOptionalStackingConfiguration;
//# sourceMappingURL=getOptionalStackingConfiguration.js.map